import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import withApiData from '../HOC/withApiData'
import colors from '../../configuration/colors'
import { hideModal } from '../../actions/modal'
import Modal from '../mini/Modal'
import Button from '../mini/Buttons/Button'
import H2 from '../typography/headers/H2'
import MarginRight15 from '../typography/margins/MarginRight15'
import CancelButton from '../mini/Buttons/CancelButton'
import { fetchResource, updateResource } from '../mycourses/Activities/API'
import SelectorComponent from './CreateOrEditCourseModal/InputComponents/SelectorComponent'
import InputLabel from '../typography/Input/InputLabel'
import TextInputComponent from './CreateOrEditCourseModal/InputComponents/TextInputComponent'
import EditCourseIcon from '../../static/images/mycourses/icon_edit_course.svg'
import { italicsOnlyMDEOptions } from '../../utils/markdowneditor'

const resourceTypeOptions = [
  { id: 1, name: 'Audio', label: 'Audio', sort_order: 0 },
  { id: 2, name: 'Document', label: 'Document', sort_order: 1 },
  { id: 3, name: 'App', label: 'game_app', sort_order: 2 },
  { id: 4, name: 'Image', label: 'Image', sort_order: 3 },
  { id: 5, name: 'Presentation', label: 'Presentation', sort_order: 4 },
  { id: 6, name: 'Video', label: 'Video', sort_order: 5 },
  { id: 7, name: 'Web', label: 'Web', sort_order: 6 },
  { id: 8, name: 'Simulation', label: 'Simulation', sort_order: 7 }
]

const ModalContainer = styled.div`
  width: 660px;
  border-radius: 10px;
  box-shadow: 0 2px 3.8px 0.2px rgba(0, 0, 0, 0.15);
  background-color: ${colors.smoke};
  margin: 75px auto;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0px;
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 0 30px 30px;
`

const Text = styled.p`
  width: 100%;
  color: ${colors.victoria};
  font-size: 16px;
  letter-spacing: 0;
  line-height: 20px;
  overflow: hidden;
`

const AddResourceModal = ({
  apiData,
  levelId,
  activityQuestId,
  resourceId
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const { isLoading: isResourceLoading, data: resource } = useQuery(
    ['resource-' + resourceId],
    () => fetchResource(apiData, levelId, activityQuestId, resourceId),
    {
      refetchOnWindowFocus: false
    }
  )

  const [resourceName, setResourceName] = useState('')
  const handleResourceName = e => {
    setResourceName(e.target.value)
  }

  const [resourceDescription, setResourceDescription] = useState(null)
  const handleResourceDescription = e => {
    let event = {
      target: {}
    }
    event.target.id = 'resource-description'
    event.target.value = e
    if (event.target.value.length === 0) {
      setResourceDescription(null)
    } else {
      setResourceDescription(event.target.value)
    }
  }

  const [selectedResourceType, setSelectedResourceType] = useState(null)
  const handleSelectedResourceType = e => {
    setSelectedResourceType(e)
  }

  useEffect(() => {
    if (resource !== undefined) {
      setResourceName(resource.name)
      setResourceDescription(
        resource.description === null ? null : resource.description
      )
      setSelectedResourceType(
        resource.resource_type !== null &&
          resourceTypeOptions.find(
            option => option.label.toLowerCase() === resource.resource_type
          )
      )
    }
  }, [resource])

  const closeTheModal = () => {
    dispatch(hideModal())
  }

  const createMutationUpdateResource = useMutation(
    ({ apiData, levelId, activityQuestId, resourceId, resource }) =>
      updateResource(apiData, levelId, activityQuestId, resourceId, resource)
  )

  const handleUpdateResource = () => {
    const resource = {
      name: resourceName,
      description: resourceDescription,
      resource_type:
        selectedResourceType.label !== 'App'
          ? selectedResourceType.label.toLowerCase()
          : 'game_app'
    }
    createMutationUpdateResource.mutate(
      {
        apiData,
        levelId,
        activityQuestId,
        resourceId,
        resource
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('resources-' + activityQuestId)
          setTimeout(() => {
            dispatch(hideModal())
          }, 300)
        }
      }
    )
  }

  const resourceDescriptionOptions = Object.assign({}, italicsOnlyMDEOptions)
  resourceDescriptionOptions.initialValue = resourceDescription
  resourceDescriptionOptions.placeholder = 'Resource Description'
  resourceDescriptionOptions.autofocus = false

  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      lockBodyScroll={false}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        {isResourceLoading ? (
          <div className="loading" />
        ) : (
          <React.Fragment>
            <HeaderContainer>
              <H2>
                <span style={{ marginRight: '12px' }}>
                  <img src={EditCourseIcon} />
                </span>
                Edit Resource
              </H2>
              <FlexContainer>
                <MarginRight15>
                  <CancelButton
                    handleClick={closeTheModal}
                    title="Cancel"
                    width="104px"
                  />
                </MarginRight15>
                <div>
                  <Button
                    width="104px"
                    title="Save"
                    handleClick={handleUpdateResource}
                    buttonColor={colors.oeYellow}
                    borderColor={colors.oeYellow}
                    hoverBackground={colors.charcoal4}
                    hoverColor={colors.white}
                    shouldShowTooltipAndDisableButton={false}
                  />
                </div>
              </FlexContainer>
            </HeaderContainer>
            <MainContainer>
              <TextInputComponent
                title="Resource Name"
                placeholder="Resource Name"
                handleEnteredTextValue={handleResourceName}
                value={resourceName}
                shouldDisplayAsteriskSymbol={true}
                shouldBeTextarea={false}
              />
              <TextInputComponent
                title="Resource Description"
                placeholder="Resource Description"
                handleEnteredTextValue={handleResourceDescription}
                value={resourceDescription}
                shouldDisplayAsteriskSymbol={false}
                shouldBeTextarea={true}
                options={resourceDescriptionOptions}
              />
              <SelectorComponent
                placeholder="Resource Type"
                handleSelectedValueChange={handleSelectedResourceType}
                selectedValue={selectedResourceType}
                options={resourceTypeOptions}
                shouldDisplayAsterisk={true}
              />
              <div>
                <InputLabel>Format</InputLabel>
                <Text>
                  {resource !== undefined && resource.file_name === null
                    ? 'Link'
                    : 'File'}
                </Text>
              </div>
              <div>
                <InputLabel>
                  {resource !== undefined && resource.file_name !== null
                    ? 'Resource File'
                    : 'URL'}
                </InputLabel>
                <Text>
                  {resource !== undefined && resource.file_name !== null
                    ? resource.file_name
                    : resource.url}
                </Text>
              </div>
            </MainContainer>
          </React.Fragment>
        )}
      </ModalContainer>
    </Modal>
  )
}

AddResourceModal.propTypes = {
  apiData: PropTypes.object.isRequired,
  levelId: PropTypes.string.isRequired,
  activityQuestId: PropTypes.string.isRequired,
  activity: PropTypes.object.isRequired
}

export default withApiData(AddResourceModal)



// WEBPACK FOOTER //
// ./src/components/modal/EditResourceModal.js